import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from "react";
import { api } from "@/lib/api";
import { clearCache, clearQueue } from "@/lib/offlineDb";
import { setSessionActor, signedOut, removeLocalDrafts } from "@/lib/sessionPrivacy";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = unknown, false = logged-out, object = logged-in
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (signedOut()) { setSessionActor(null); setUser(false); setLoading(false); return; }
        try {
            const { data } = await api.get("/auth/me", { skipAuthRedirect: true });
            if (signedOut()) return;
            setSessionActor(data);
            setUser(data);
        } catch {
            setSessionActor(null);
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        const pendingLogout = () => { if (signedOut()) api.post("/auth/logout", {}, { skipAuthRedirect: true }).catch(() => {}); };
        const changed = event => { if (event.key === "vt_signed_out" || event.key === "vt_session_owner") { setSessionActor(null); setUser(false); } };
        window.addEventListener("online", pendingLogout);
        window.addEventListener("storage", changed);
        return () => { window.removeEventListener("online", pendingLogout); window.removeEventListener("storage", changed); };
    }, [refresh]);

    // Apply Senior Mode globally via a root class (boosts base font size).
    // NOTE: the root class is owned by SeniorModeContext (single source of
    // truth) — AuthContext no longer toggles it to avoid override races.

    const login = useCallback((userObj) => {
        localStorage.removeItem("vt_signed_out");
        setSessionActor(userObj);
        setUser(userObj);
    }, []);

    const logout = useCallback(() => {
        localStorage.setItem("vt_signed_out", "true");
        setSessionActor(null);
        removeLocalDrafts();
        Promise.all([clearCache(), clearQueue()]);
        api.post("/auth/logout", {}, { skipAuthRedirect: true }).catch(() => {});
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, logout, refresh }),
        [user, loading, login, logout, refresh]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
